import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { Router } from '@angular/router';
import { ApiService } from './api.service';
import { AdminInfo, LoginRequest, LoginResponse } from '../models';

const ADMIN_KEY = 'adminInfo';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private api    = inject(ApiService);
  private router = inject(Router);

  private _admin = signal<AdminInfo | null>(this.loadAdmin());
  readonly admin = this._admin.asReadonly();

  // ── Login — store tokens then fetch full admin info ───────────────────────
  login(payload: LoginRequest): Observable<LoginResponse> {
    return this.api.post<LoginResponse>('/auth/login', payload).pipe(
      tap(data => {
        localStorage.setItem('token',        data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        // Fetch full admin profile after token is stored
        this.fetchMe().subscribe();
      })
    );
  }

  // ── GET /auth/me ──────────────────────────────────────────────────────────
  fetchMe(): Observable<AdminInfo> {
    return this.api.get<AdminInfo>('/auth/me').pipe(
      tap(admin => {
        localStorage.setItem(ADMIN_KEY, JSON.stringify(admin));
        this._admin.set(admin);
      })
    );
  }

  // ── Logout ────────────────────────────────────────────────────────────────
  logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem(ADMIN_KEY);
    this._admin.set(null);
    this.router.navigate(['/auth/login']);
  }

  isLoggedIn(): boolean {
    return !!localStorage.getItem('token');
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  private loadAdmin(): AdminInfo | null {
    try {
      const raw = localStorage.getItem(ADMIN_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
}